import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiCheckCircle,
  FiCopy,
  FiEye,
  FiKey,
  FiMail,
  FiPlus,
  FiRefreshCw,
  FiShield,
  FiUser,
  FiX,
} from "react-icons/fi";

import Config from "../../../../../../config";

import EmployeePageHeader from "../common/EmployeePageHeader";
import EmployeeModuleTabs from "../common/EmployeeModuleTabs";
import { employeeOfferApi } from "../utils/employeeApi";

import "./EmployeeOfferManagement.css";


const EMPTY = {
  employeeId: "",
  salaryMonthly: "",
  probationMonths: 3,
  noticePeriodDays: 30,
  salaryPaymentWindow:
    "Between the 5th and 10th of each month",
  workLocation: "",
  reportingTo: "",
  responsibilities: "",
  additionalTerms: "",
  validDays: 30,
};


const getData =
  response =>
    response?.data ??
    response ??
    {};


const apiBase =
  String(
    Config.site_api ||
    ""
  ).replace(
    /\/$/,
    ""
  );


const toDocumentUrl =
  path => {

    const value =
      String(
        path ||
        ""
      )
        .trim()
        .replace(
          /\\/g,
          "/"
        );


    if (
      !value
    ) {
      return "";
    }


    if (
      /^(https?:|blob:|data:)/i.test(
        value
      )
    ) {
      return value;
    }


    return `${apiBase}/${value.replace(
      /^\/+/,
      ""
    )}`;

  };


const maskAadhaar =
  value => {

    const digits =
      String(
        value ||
        ""
      ).replace(
        /\D/g,
        ""
      );


    if (
      !digits
    ) {
      return "-";
    }


    return `XXXX XXXX ${digits.slice(-4)}`;

  };


const maskAccount =
  value => {

    const text =
      String(
        value ||
        ""
      ).trim();


    if (
      !text
    ) {
      return "-";
    }


    return `XXXXXX${text.slice(-4)}`;

  };


const statusLabel =
  value =>
    String(
      value ||
      ""
    )
      .replace(
        /_/g,
        " "
      );


const EmployeeOfferManagement =
  () => {

    const [
      rows,
      setRows,
    ] =
      useState([]);

    const [
      employees,
      setEmployees,
    ] =
      useState([]);

    const [
      roles,
      setRoles,
    ] =
      useState([]);

    const [
      loading,
      setLoading,
    ] =
      useState(false);

    const [
      form,
      setForm,
    ] =
      useState(
        EMPTY
      );

    const [
      show,
      setShow,
    ] =
      useState(false);

    const [
      message,
      setMessage,
    ] =
      useState("");

    const [
      summary,
      setSummary,
    ] =
      useState({});

    const [
      reviewOffer,
      setReviewOffer,
    ] =
      useState(null);

    const [
      credentialsOffer,
      setCredentialsOffer,
    ] =
      useState(null);

    const [
      selectedRoleId,
      setSelectedRoleId,
    ] =
      useState("");

    const [
      actionLoading,
      setActionLoading,
    ] =
      useState(false);


    const load =
      useCallback(
        async () => {

          setLoading(
            true
          );

          setMessage(
            ""
          );


          try {

            const [
              offerResponse,
              dropdownResponse,
            ] =
              await Promise.all([
                employeeOfferApi
                  .getList({
                    page: 1,
                    limit: 100,
                  }),

                employeeOfferApi
                  .getDropdownData(),
              ]);


            const offerData =
              getData(
                offerResponse
              );

            const dropdownData =
              getData(
                dropdownResponse
              );


            setRows(
              Array.isArray(
                offerData?.records
              )
                ?
                offerData.records
                :
                []
            );

            setSummary(
              offerData?.summary ||
              {}
            );

            setEmployees(
              Array.isArray(
                dropdownData?.employees
              )
                ?
                dropdownData.employees
                :
                []
            );

            setRoles(
              Array.isArray(
                dropdownData?.roles
              )
                ?
                dropdownData.roles
                :
                []
            );

          } catch (
            error
          ) {

            setMessage(
              error?.message ||
              "Unable to load offer letters."
            );

          } finally {

            setLoading(
              false
            );

          }

        },
        []
      );


    useEffect(
      () => {

        load();

      },
      [
        load,
      ]
    );


    const selectedEmployee =
      useMemo(
        () =>
          employees.find(
            employee =>
              String(
                employee._id ||
                employee.id
              ) ===
              String(
                form.employeeId
              )
          ) ||
          null,
        [
          employees,
          form.employeeId,
        ]
      );


    useEffect(
      () => {

        if (
          !selectedEmployee
        ) {
          return;
        }


        setForm(
          previous => ({
            ...previous,

            salaryMonthly:
              selectedEmployee
                .salary
                ?.netSalary ??
              selectedEmployee
                .salary
                ?.basicSalary ??
              previous
                .salaryMonthly,

            workLocation:
              previous
                .workLocation,
          })
        );

      },
      [
        selectedEmployee,
      ]
    );


    const createOffer =
      async event => {

        event.preventDefault();


        try {

          const payload = {
            ...form,

            salaryMonthly:
              Number(
                form.salaryMonthly ||
                0
              ),

            probationMonths:
              Number(
                form.probationMonths ||
                0
              ),

            noticePeriodDays:
              Number(
                form.noticePeriodDays ||
                30
              ),

            validDays:
              Number(
                form.validDays ||
                30
              ),

            responsibilities:
              String(
                form.responsibilities ||
                ""
              )
                .split(
                  "\n"
                )
                .map(
                  item =>
                    item.trim()
                )
                .filter(
                  Boolean
                ),
          };


          const response =
            await employeeOfferApi
              .create(
                form.employeeId,
                payload
              );


          const data =
            getData(
              response
            );


          setShow(
            false
          );

          setForm(
            EMPTY
          );


          await load();


          if (
            data?.publicLink
          ) {

            await navigator
              .clipboard
              ?.writeText(
                data.publicLink
              );


            setMessage(
              "Offer created. Secure link copied."
            );

          }

        } catch (
          error
        ) {

          setMessage(
            error?.message ||
            "Unable to create offer."
          );

        }

      };


    const employeeForOffer =
      offer =>
        employees.find(
          employee =>
            String(
              employee._id ||
              employee.id
            ) ===
            String(
              offer?.employeeId
            )
        ) ||
        null;


    const copyLink =
      async row => {

        try {

          const response =
            await employeeOfferApi
              .getLink(
                row._id
              );


          const data =
            getData(
              response
            );


          if (
            data?.publicLink
          ) {

            await navigator
              .clipboard
              ?.writeText(
                data.publicLink
              );


            setMessage(
              "Secure employee offer link copied."
            );

          }

        } catch (
          error
        ) {

          setMessage(
            error?.message ||
            "Unable to copy offer link."
          );

        }

      };


    const sendEmail =
      async row => {

        setActionLoading(
          true
        );


        try {

          const response =
            await employeeOfferApi
              .sendEmail(
                row._id
              );


          setMessage(
            response?.message ||
            "Offer email sent."
          );


          await load();

        } catch (
          error
        ) {

          setMessage(
            error?.message ||
            "Unable to send offer email."
          );

        } finally {

          setActionLoading(
            false
          );

        }

      };


    const approveOnboarding =
      async offer => {

        if (
          offer?.status !==
          "DETAILS_SUBMITTED"
        ) {
          return;
        }


        setActionLoading(
          true
        );


        try {

          const response =
            await employeeOfferApi
              .verify(
                offer._id
              );


          setMessage(
            response?.message ||
            "Employee onboarding approved."
          );


          setReviewOffer(
            null
          );


          await load();

        } catch (
          error
        ) {

          setMessage(
            error?.message ||
            "Unable to approve onboarding."
          );

        } finally {

          setActionLoading(
            false
          );

        }

      };


    const openCredentials =
      offer => {

        if (
          ![
            "VERIFIED",
            "CREDENTIALS_SENT",
          ].includes(
            offer?.status
          )
        ) {
          return;
        }


        setSelectedRoleId(
          ""
        );

        setCredentialsOffer(
          offer
        );

      };


    const sendCredentials =
      async () => {

        if (
          !credentialsOffer ||
          !selectedRoleId
        ) {
          return;
        }


        setActionLoading(
          true
        );


        try {

          const response =
            await employeeOfferApi
              .sendCredentials(
                credentialsOffer._id,
                {
                  roleId:
                    selectedRoleId,
                }
              );


          setMessage(
            response?.message ||
            (
              credentialsOffer.status ===
              "CREDENTIALS_SENT"
                ?
                "Password reset and new credentials emailed."
                :
                "Employee panel credentials created and emailed."
            )
          );


          setCredentialsOffer(
            null
          );

          setSelectedRoleId(
            ""
          );


          await load();

        } catch (
          error
        ) {

          setMessage(
            error?.message ||
            "Unable to create/send employee credentials."
          );

        } finally {

          setActionLoading(
            false
          );

        }

      };


    const cards = [
      [
        "Draft",
        summary.DRAFT ||
        0,
      ],
      [
        "Sent / Opened",
        (
          summary.SENT ||
          0
        ) +
        (
          summary.OPENED ||
          0
        ),
      ],
      [
        "Accepted",
        summary.ACCEPTED ||
        0,
      ],
      [
        "Awaiting Review",
        summary.DETAILS_SUBMITTED ||
        0,
      ],
      [
        "Completed",
        (
          summary.VERIFIED ||
          0
        ) +
        (
          summary.CREDENTIALS_SENT ||
          0
        ),
      ],
    ];


    const reviewEmployee =
      employeeForOffer(
        reviewOffer
      );

    const reviewDetails =
      reviewOffer
        ?.personalDetails ||
      {};

    const reviewDocuments =
      Array.isArray(
        reviewOffer
          ?.documents
      )
        ?
        reviewOffer.documents
        :
        [];


    return (

      <div className="employee-offer-page">

        <EmployeePageHeader
          title="Offer & Onboarding"
          description="Create employment offers, review submitted employee details and documents, approve onboarding and activate panel access."
          onRefresh={
            load
          }
          loading={
            loading
          }
          hideAction
        />


        <EmployeeModuleTabs />


        <div className="employee-offer-summary">

          {
            cards.map(
              (
                [
                  label,
                  value
                ]
              ) => (
                <article key={label}>
                  <span>
                    {label}
                  </span>
                  <strong>
                    {value}
                  </strong>
                </article>
              )
            )
          }

        </div>


        <div className="employee-offer-head">

          <div>

            <h2>
              Employment Offers
            </h2>

            <p>
              Employee panel credentials are available only after submitted onboarding details have been reviewed and approved.
            </p>

          </div>


          <div>

            <button
              type="button"
              onClick={
                load
              }
            >
              <FiRefreshCw />
              Refresh
            </button>


            <button
              type="button"
              className="primary"
              onClick={
                () =>
                  setShow(
                    true
                  )
              }
            >
              <FiPlus />
              New Offer
            </button>

          </div>

        </div>


        {
          message
            ?
            (
              <div className="employee-offer-message">
                {message}
              </div>
            )
            :
            null
        }


        <div className="employee-offer-table-wrap">

          <table>

            <thead>
              <tr>
                <th>Offer</th>
                <th>Employee</th>
                <th>Designation</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>


            <tbody>

              {
                !loading &&
                rows.map(
                  row => (
                    <tr key={row._id}>

                      <td>
                        <b>
                          {row.offerNumber}
                        </b>
                      </td>


                      <td>
                        {
                          row.candidateSnapshot
                            ?.name
                        }

                        <small>
                          {
                            row.candidateSnapshot
                              ?.email ||
                            row.candidateSnapshot
                              ?.mobile
                          }
                        </small>
                      </td>


                      <td>
                        {
                          row.candidateSnapshot
                            ?.designation ||
                          "-"
                        }
                      </td>


                      <td>
                        <span
                          className={`offer-status ${String(
                            row.status
                          ).toLowerCase()}`}
                        >
                          {
                            statusLabel(
                              row.status
                            )
                          }
                        </span>
                      </td>


                      <td>
                        {
                          new Date(
                            row.createdAt
                          )
                            .toLocaleDateString(
                              "en-IN"
                            )
                        }
                      </td>


                      <td className="actions">

                        <button
                          type="button"
                          title="Review offer / employee details"
                          className={
                            row.status ===
                            "DETAILS_SUBMITTED"
                              ?
                              "needs-review"
                              :
                              ""
                          }
                          onClick={
                            () =>
                              setReviewOffer(
                                row
                              )
                          }
                        >
                          <FiEye />
                        </button>


                        <button
                          type="button"
                          title="Copy secure link"
                          onClick={
                            () =>
                              copyLink(
                                row
                              )
                          }
                        >
                          <FiCopy />
                        </button>


                        <button
                          type="button"
                          title="Send offer email"
                          disabled={
                            actionLoading
                          }
                          onClick={
                            () =>
                              sendEmail(
                                row
                              )
                          }
                        >
                          <FiMail />
                        </button>

                      </td>

                    </tr>
                  )
                )
              }


              {
                !loading &&
                !rows.length
                  ?
                  (
                    <tr>
                      <td
                        colSpan="6"
                        className="empty"
                      >
                        No offer letters yet.
                      </td>
                    </tr>
                  )
                  :
                  null
              }


              {
                loading
                  ?
                  (
                    <tr>
                      <td
                        colSpan="6"
                        className="empty"
                      >
                        Loading...
                      </td>
                    </tr>
                  )
                  :
                  null
              }

            </tbody>

          </table>

        </div>


        {/* ============================================================
            CREATE OFFER
        ============================================================ */}

        {
          show
            ?
            (
              <div className="offer-modal-backdrop">

                <form
                  className="offer-modal"
                  onSubmit={
                    createOffer
                  }
                >

                  <div className="offer-modal-title">

                    <div>
                      <small>
                        EMPLOYMENT OFFER
                      </small>

                      <h3>
                        Create Offer Letter
                      </h3>
                    </div>


                    <button
                      type="button"
                      className="icon-close"
                      onClick={
                        () =>
                          setShow(
                            false
                          )
                      }
                    >
                      <FiX />
                    </button>

                  </div>


                  <label>
                    Employee

                    <select
                      required
                      value={
                        form.employeeId
                      }
                      onChange={
                        event =>
                          setForm({
                            ...form,
                            employeeId:
                              event.target.value,
                          })
                      }
                    >
                      <option value="">
                        Select employee
                      </option>

                      {
                        employees.map(
                          employee => (
                            <option
                              key={employee._id}
                              value={employee._id}
                            >
                              {employee.employeeCode} - {employee.name} - {employee.designation}
                            </option>
                          )
                        )
                      }
                    </select>
                  </label>


                  <div className="grid">

                    <label>
                      Monthly Salary / Stipend
                      <input
                        type="number"
                        min="0"
                        value={
                          form.salaryMonthly
                        }
                        onChange={
                          event =>
                            setForm({
                              ...form,
                              salaryMonthly:
                                event.target.value,
                            })
                        }
                      />
                    </label>


                    <label>
                      Probation (months)
                      <input
                        type="number"
                        min="0"
                        value={
                          form.probationMonths
                        }
                        onChange={
                          event =>
                            setForm({
                              ...form,
                              probationMonths:
                                event.target.value,
                            })
                        }
                      />
                    </label>


                    <label>
                      Notice Period (days)
                      <input
                        type="number"
                        min="0"
                        value={
                          form.noticePeriodDays
                        }
                        onChange={
                          event =>
                            setForm({
                              ...form,
                              noticePeriodDays:
                                event.target.value,
                            })
                        }
                      />
                    </label>


                    <label>
                      Offer Link Validity (days)
                      <input
                        type="number"
                        min="1"
                        value={
                          form.validDays
                        }
                        onChange={
                          event =>
                            setForm({
                              ...form,
                              validDays:
                                event.target.value,
                            })
                        }
                      />
                    </label>

                  </div>


                  <label>
                    Salary Payment Window
                    <input
                      value={
                        form.salaryPaymentWindow
                      }
                      onChange={
                        event =>
                          setForm({
                            ...form,
                            salaryPaymentWindow:
                              event.target.value,
                          })
                      }
                    />
                  </label>


                  <label>
                    Work Location
                    <input
                      value={
                        form.workLocation
                      }
                      onChange={
                        event =>
                          setForm({
                            ...form,
                            workLocation:
                              event.target.value,
                          })
                      }
                    />
                  </label>


                  <label>
                    Reporting To
                    <input
                      value={
                        form.reportingTo
                      }
                      onChange={
                        event =>
                          setForm({
                            ...form,
                            reportingTo:
                              event.target.value,
                          })
                      }
                    />
                  </label>


                  <label>
                    Key Responsibilities (one per line)
                    <textarea
                      rows="5"
                      value={
                        form.responsibilities
                      }
                      onChange={
                        event =>
                          setForm({
                            ...form,
                            responsibilities:
                              event.target.value,
                          })
                      }
                    />
                  </label>


                  <label>
                    Additional Terms
                    <textarea
                      rows="3"
                      value={
                        form.additionalTerms
                      }
                      onChange={
                        event =>
                          setForm({
                            ...form,
                            additionalTerms:
                              event.target.value,
                          })
                      }
                    />
                  </label>


                  <div className="offer-standard-terms">

                    <strong>
                      Standard clauses automatically included
                    </strong>

                    <span>
                      Confidentiality • professional conduct • verification of documents • company policies • applicable statutory requirements
                    </span>

                  </div>


                  <div className="modal-actions">

                    <button
                      type="button"
                      onClick={
                        () =>
                          setShow(
                            false
                          )
                      }
                    >
                      Cancel
                    </button>


                    <button
                      className="primary"
                      type="submit"
                    >
                      Create Offer
                    </button>

                  </div>

                </form>

              </div>
            )
            :
            null
        }


        {/* ============================================================
            EMPLOYEE / ONBOARDING REVIEW
        ============================================================ */}

        {
          reviewOffer
            ?
            (
              <div className="offer-review-backdrop">

                <aside className="offer-review-drawer">

                  <header className="offer-review-header">

                    <div>

                      <small>
                        EMPLOYEE ONBOARDING REVIEW
                      </small>

                      <h2>
                        {
                          reviewOffer
                            .candidateSnapshot
                            ?.name ||
                          "Employee"
                        }
                      </h2>

                      <p>
                        {
                          reviewOffer
                            .offerNumber
                        } • {
                          statusLabel(
                            reviewOffer
                              .status
                          )
                        }
                      </p>

                    </div>


                    <button
                      type="button"
                      onClick={
                        () =>
                          setReviewOffer(
                            null
                          )
                      }
                    >
                      <FiX />
                    </button>

                  </header>


                  <div className="offer-review-body">

                    <section className="review-card">

                      <div className="review-section-title">

                        <FiUser />

                        <div>
                          <h3>
                            Employee Master
                          </h3>
                          <p>
                            Existing employee record
                          </p>
                        </div>

                      </div>


                      <div className="review-grid">

                        <div>
                          <span>Employee Code</span>
                          <strong>
                            {
                              reviewEmployee
                                ?.employeeCode ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Name</span>
                          <strong>
                            {
                              reviewEmployee
                                ?.name ||
                              reviewOffer
                                .candidateSnapshot
                                ?.name ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Mobile</span>
                          <strong>
                            {
                              reviewEmployee
                                ?.mobile ||
                              reviewOffer
                                .candidateSnapshot
                                ?.mobile ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Email</span>
                          <strong>
                            {
                              reviewEmployee
                                ?.email ||
                              reviewOffer
                                .candidateSnapshot
                                ?.email ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Department</span>
                          <strong>
                            {
                              reviewEmployee
                                ?.department ||
                              reviewOffer
                                .candidateSnapshot
                                ?.department ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Designation</span>
                          <strong>
                            {
                              reviewEmployee
                                ?.designation ||
                              reviewOffer
                                .candidateSnapshot
                                ?.designation ||
                              "-"
                            }
                          </strong>
                        </div>

                      </div>

                    </section>


                    <section className="review-card">

                      <div className="review-section-title">

                        <FiCheckCircle />

                        <div>
                          <h3>
                            Submitted Personal Details
                          </h3>
                          <p>
                            Information entered by the employee
                          </p>
                        </div>

                      </div>


                      {
                        reviewOffer.status ===
                        "ACCEPTED" &&
                        !reviewOffer
                          .detailsSubmittedAt
                          ?
                          (
                            <div className="review-waiting">
                              Offer accepted. Waiting for the employee to complete and submit onboarding details.
                            </div>
                          )
                          :
                          (
                            <div className="review-grid">

                              <div>
                                <span>Full Name</span>
                                <strong>
                                  {
                                    reviewDetails
                                      .fullName ||
                                    "-"
                                  }
                                </strong>
                              </div>

                              <div>
                                <span>Father / Guardian</span>
                                <strong>
                                  {
                                    reviewDetails
                                      .fatherName ||
                                    "-"
                                  }
                                </strong>
                              </div>

                              <div>
                                <span>Date of Birth</span>
                                <strong>
                                  {
                                    reviewDetails
                                      .dateOfBirth
                                      ?
                                      new Date(
                                        reviewDetails
                                          .dateOfBirth
                                      )
                                        .toLocaleDateString(
                                          "en-IN"
                                        )
                                      :
                                      "-"
                                  }
                                </strong>
                              </div>

                              <div>
                                <span>Gender</span>
                                <strong>
                                  {
                                    reviewDetails
                                      .gender ||
                                    "-"
                                  }
                                </strong>
                              </div>

                              <div>
                                <span>Mobile</span>
                                <strong>
                                  {
                                    reviewDetails
                                      .mobile ||
                                    "-"
                                  }
                                </strong>
                              </div>

                              <div>
                                <span>Alternate Mobile</span>
                                <strong>
                                  {
                                    reviewDetails
                                      .alternateMobile ||
                                    "-"
                                  }
                                </strong>
                              </div>

                              <div className="wide">
                                <span>Email</span>
                                <strong>
                                  {
                                    reviewDetails
                                      .email ||
                                    "-"
                                  }
                                </strong>
                              </div>

                              <div className="wide">
                                <span>Address</span>
                                <strong>
                                  {
                                    [
                                      reviewDetails
                                        .addressLine,
                                      reviewDetails
                                        .addressLine2,
                                      reviewDetails
                                        .city,
                                      reviewDetails
                                        .state,
                                      reviewDetails
                                        .pincode,
                                    ]
                                      .filter(
                                        Boolean
                                      )
                                      .join(
                                        ", "
                                      ) ||
                                    "-"
                                  }
                                </strong>
                              </div>

                            </div>
                          )
                      }

                    </section>


                    <section className="review-card">

                      <div className="review-section-title">

                        <FiShield />

                        <div>
                          <h3>
                            Identity & Bank Verification
                          </h3>
                          <p>
                            Sensitive details are partially masked in the admin UI
                          </p>
                        </div>

                      </div>


                      <div className="review-grid">

                        <div>
                          <span>Aadhaar</span>
                          <strong>
                            {
                              maskAadhaar(
                                reviewDetails
                                  .aadhaarLast4
                              )
                            }
                          </strong>
                        </div>

                        <div>
                          <span>PAN</span>
                          <strong>
                            {
                              reviewDetails
                                .panNumber ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Account Holder</span>
                          <strong>
                            {
                              reviewDetails
                                .accountHolderName ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Bank</span>
                          <strong>
                            {
                              reviewDetails
                                .bankName ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Account No.</span>
                          <strong>
                            {
                              reviewDetails
                                .accountNumberMasked ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>IFSC</span>
                          <strong>
                            {
                              reviewDetails
                                .ifsc ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Emergency Contact</span>
                          <strong>
                            {
                              reviewDetails
                                .emergencyName ||
                              "-"
                            }
                          </strong>
                        </div>

                        <div>
                          <span>Emergency Mobile</span>
                          <strong>
                            {
                              reviewDetails
                                .emergencyMobile ||
                              "-"
                            }
                          </strong>
                        </div>

                      </div>

                    </section>


                    <section className="review-card">

                      <div className="review-section-title">

                        <FiShield />

                        <div>
                          <h3>
                            Uploaded Documents
                          </h3>
                          <p>
                            Open each document before approving onboarding
                          </p>
                        </div>

                      </div>


                      {
                        reviewDocuments.length
                          ?
                          (
                            <div className="document-list">

                              {
                                reviewDocuments.map(
                                  document => (
                                    <a
                                      key={
                                        document._id ||
                                        `${document.type}-${document.path}`
                                      }
                                      href={
                                        toDocumentUrl(
                                          document.path
                                        )
                                      }
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <span>
                                        {
                                          String(
                                            document.type ||
                                            "DOCUMENT"
                                          )
                                            .replace(
                                              /_/g,
                                              " "
                                            )
                                        }
                                      </span>

                                      <small>
                                        {
                                          document.originalName ||
                                          document.label ||
                                          "View document"
                                        }
                                      </small>
                                    </a>
                                  )
                                )
                              }

                            </div>
                          )
                          :
                          (
                            <div className="review-waiting">
                              No documents have been submitted yet.
                            </div>
                          )
                      }

                    </section>


                    <section className="review-card review-card--decision">

                      <h3>
                        Admin Decision
                      </h3>


                      {
                        reviewOffer.status ===
                        "DETAILS_SUBMITTED"
                          ?
                          (
                            <>
                              <p>
                                Review the employee information and every uploaded document before approving onboarding.
                              </p>

                              <button
                                type="button"
                                className="approve-button"
                                disabled={
                                  actionLoading
                                }
                                onClick={
                                  () =>
                                    approveOnboarding(
                                      reviewOffer
                                    )
                                }
                              >
                                <FiShield />
                                {
                                  actionLoading
                                    ?
                                    "Approving..."
                                    :
                                    "Approve Onboarding"
                                }
                              </button>
                            </>
                          )
                          :
                          null
                      }


                      {
                        reviewOffer.status ===
                        "ACCEPTED"
                          ?
                          (
                            <div className="review-waiting">
                              Employee has accepted the offer but has not submitted onboarding details. Approval and credentials remain disabled.
                            </div>
                          )
                          :
                          null
                      }


                      {
                        [
                          "VERIFIED",
                          "CREDENTIALS_SENT",
                        ].includes(
                          reviewOffer.status
                        )
                          ?
                          (
                            <div className="review-approved">

                              <FiCheckCircle />

                              <div>
                                <strong>
                                  Onboarding Approved
                                </strong>

                                <span>
                                  {
                                    reviewOffer
                                      .verifiedAt
                                      ?
                                      `Approved ${new Date(
                                        reviewOffer
                                          .verifiedAt
                                      ).toLocaleString(
                                        "en-IN"
                                      )}`
                                      :
                                      "Employee details have been verified."
                                  }
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={
                                  () =>
                                    openCredentials(
                                      reviewOffer
                                    )
                                }
                              >
                                <FiKey />
                                {
                                  reviewOffer.status ===
                                  "CREDENTIALS_SENT"
                                    ?
                                    "Reset Password & Send"
                                    :
                                    "Create & Send Panel Credentials"
                                }
                              </button>

                            </div>
                          )
                          :
                          null
                      }


                      {
                        reviewOffer.status ===
                        "CREDENTIALS_SENT" &&
                        reviewOffer
                          .credentialsSentAt
                          ?
                          (
                            <p className="credentials-sent-note">
                              Credentials last sent: {
                                new Date(
                                  reviewOffer
                                    .credentialsSentAt
                                )
                                  .toLocaleString(
                                    "en-IN"
                                  )
                              }
                            </p>
                          )
                          :
                          null
                      }

                    </section>

                  </div>

                </aside>

              </div>
            )
            :
            null
        }


        {/* ============================================================
            PROFESSIONAL CREDENTIAL CONFIRMATION
        ============================================================ */}

        {
          credentialsOffer
            ?
            (
              <div className="credential-modal-backdrop">

                <div className="credential-modal">

                  <header>

                    <div className="credential-icon">
                      <FiKey />
                    </div>

                    <div>

                      <small>
                        EMPLOYEE PANEL ACCESS
                      </small>

                      <h3>
                        {
                          credentialsOffer.status ===
                          "CREDENTIALS_SENT"
                            ?
                            "Reset Employee Password"
                            :
                            "Create & Send Credentials"
                        }
                      </h3>

                    </div>


                    <button
                      type="button"
                      className="icon-close"
                      onClick={
                        () =>
                          setCredentialsOffer(
                            null
                          )
                      }
                    >
                      <FiX />
                    </button>

                  </header>


                  <div className="credential-modal-body">

                    {
                      credentialsOffer.status ===
                      "CREDENTIALS_SENT"
                        ?
                        (
                          <div className="credential-warning">
                            A new temporary password will be generated. The employee's existing password will stop working.
                          </div>
                        )
                        :
                        (
                          <div className="credential-info">
                            A panel login account will be created and a temporary password will be emailed to the employee.
                          </div>
                        )
                    }


                    <div className="credential-employee">

                      <strong>
                        {
                          credentialsOffer
                            .candidateSnapshot
                            ?.name ||
                          "Employee"
                        }
                      </strong>

                      <span>
                        {
                          credentialsOffer
                            .candidateSnapshot
                            ?.designation ||
                          "-"
                        }
                      </span>

                      <small>
                        {
                          credentialsOffer
                            .personalDetails
                            ?.email ||
                          credentialsOffer
                            .candidateSnapshot
                            ?.email ||
                          "-"
                        } • {
                          credentialsOffer
                            .personalDetails
                            ?.mobile ||
                          credentialsOffer
                            .candidateSnapshot
                            ?.mobile ||
                          "-"
                        }
                      </small>

                    </div>


                    <label className="credential-role">

                      <span>
                        Employee Login Role *
                      </span>

                      <select
                        value={
                          selectedRoleId
                        }
                        onChange={
                          event =>
                            setSelectedRoleId(
                              event.target.value
                            )
                        }
                      >

                        <option value="">
                          Select role
                        </option>

                        {
                          roles.map(
                            role => (
                              <option
                                key={
                                  role._id ||
                                  role.id
                                }
                                value={
                                  role._id ||
                                  role.id
                                }
                              >
                                {role.name} ({role.code})
                              </option>
                            )
                          )
                        }

                      </select>

                      <small>
                        Permissions are inherited from the selected active tenant role.
                      </small>

                    </label>


                    <div className="credential-summary">

                      <div>
                        <span>
                          Email
                        </span>

                        <strong>
                          {
                            credentialsOffer
                              .personalDetails
                              ?.email ||
                            credentialsOffer
                              .candidateSnapshot
                              ?.email ||
                            "-"
                          }
                        </strong>
                      </div>

                      <div>
                        <span>
                          Mobile / Login
                        </span>

                        <strong>
                          {
                            credentialsOffer
                              .personalDetails
                              ?.mobile ||
                            credentialsOffer
                              .candidateSnapshot
                              ?.mobile ||
                            "-"
                          }
                        </strong>
                      </div>

                    </div>

                  </div>


                  <footer>

                    <button
                      type="button"
                      onClick={
                        () =>
                          setCredentialsOffer(
                            null
                          )
                      }
                    >
                      Cancel
                    </button>


                    <button
                      type="button"
                      className="primary"
                      disabled={
                        !selectedRoleId ||
                        actionLoading
                      }
                      onClick={
                        sendCredentials
                      }
                    >
                      <FiKey />
                      {
                        actionLoading
                          ?
                          "Processing..."
                          :
                          credentialsOffer.status ===
                          "CREDENTIALS_SENT"
                            ?
                            "Reset & Email Password"
                            :
                            "Create & Send Credentials"
                      }
                    </button>

                  </footer>

                </div>

              </div>
            )
            :
            null
        }

      </div>

    );

  };


export default EmployeeOfferManagement;
